import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminAccessGroupsService } from "@/services/admin-access-groups-service";
import { tenantAdminService } from "@/services/tenant-admin-service";
import type {
  AccessGroupDetail,
  AccessGroupDetailItem,
  AccessGroupListQuery,
  AccessGroupScope,
  AccessGroupSummary,
  AdminAccessGroupDetail,
  AdminAccessGroupSummary,
  CreateAccessGroupDto,
  CreateAccessGroupPayload,
  PaginatedAccessGroupsResponse,
  UpdateAccessGroupDto,
} from "@/types/access-group";

export const accessGroupKeys = {
  all: ["access-groups"] as const,
  lists: (scope: AccessGroupScope = "tenant") =>
    [...accessGroupKeys.all, scope, "list"] as const,
  list: (scope: AccessGroupScope, query?: AccessGroupListQuery) =>
    [...accessGroupKeys.lists(scope), query] as const,
  filterOptions: (scope: AccessGroupScope) =>
    [...accessGroupKeys.all, scope, "filter-options"] as const,
  details: (scope: AccessGroupScope = "tenant") =>
    [...accessGroupKeys.all, scope, "detail"] as const,
  detail: (scope: AccessGroupScope, id: string) =>
    [...accessGroupKeys.details(scope), id] as const,
  userGroups: (userId: string) =>
    [...accessGroupKeys.all, "admin", "user", userId] as const,
};

export const useAccessGroupFilterOptions = (
  scope: AccessGroupScope,
  enabled = true,
) =>
  useQuery({
    queryKey: accessGroupKeys.filterOptions(scope),
    queryFn: async () => {
      if (scope === "admin") {
        const options =
          await adminAccessGroupsService.getAccessGroupFilterOptions();
        return { entities: options.tenants, users: options.users };
      }

      const options = await tenantAdminService.getAccessGroupFilterOptions();
      return { entities: options.hotels, users: options.users };
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  });

const isAccessGroupScope = (value: unknown): value is AccessGroupScope =>
  value === "tenant" || value === "admin";

const resolveAccessGroupsArgs = (
  scopeOrQuery?: AccessGroupScope | AccessGroupListQuery,
  queryOrEnabled?: AccessGroupListQuery | boolean,
  enabled = true,
): {
  scope: AccessGroupScope;
  query?: AccessGroupListQuery;
  enabled: boolean;
} => {
  if (isAccessGroupScope(scopeOrQuery)) {
    return {
      scope: scopeOrQuery,
      query: typeof queryOrEnabled === "object" ? queryOrEnabled : undefined,
      enabled: typeof queryOrEnabled === "boolean" ? queryOrEnabled : enabled,
    };
  }

  return {
    scope: "tenant",
    query: scopeOrQuery,
    enabled: typeof queryOrEnabled === "boolean" ? queryOrEnabled : enabled,
  };
};

const resolveAccessGroupDetailArgs = (
  scopeOrId: AccessGroupScope | string,
  id?: string,
): { scope: AccessGroupScope; id: string } => {
  if (isAccessGroupScope(scopeOrId)) {
    return { scope: scopeOrId, id: id ?? "" };
  }

  return { scope: "tenant", id: scopeOrId };
};

export const useAccessGroups = (
  scopeOrQuery?: AccessGroupScope | AccessGroupListQuery,
  queryOrEnabled?: AccessGroupListQuery | boolean,
  enabled = true,
) => {
  const args = resolveAccessGroupsArgs(scopeOrQuery, queryOrEnabled, enabled);

  return useQuery<PaginatedAccessGroupsResponse>({
    queryKey: accessGroupKeys.list(args.scope, args.query),
    queryFn: () =>
      args.scope === "admin"
        ? adminAccessGroupsService.getAccessGroups(args.query)
        : tenantAdminService.getAccessGroups(args.query),
    staleTime: 5 * 60 * 1000,
    enabled: args.enabled,
  });
};

export const useAccessGroup = (
  scopeOrId: AccessGroupScope | string,
  id?: string,
) => {
  const args = resolveAccessGroupDetailArgs(scopeOrId, id);

  return useQuery<AccessGroupDetailItem>({
    queryKey: accessGroupKeys.detail(args.scope, args.id),
    queryFn: () =>
      args.scope === "admin"
        ? adminAccessGroupsService.getAccessGroupById(args.id)
        : tenantAdminService.getAccessGroupById(args.id),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(args.id),
  });
};

export const useAdminUserAccessGroups = (userId: string, enabled = true) =>
  useQuery({
    queryKey: accessGroupKeys.userGroups(userId),
    queryFn: () => adminAccessGroupsService.getUserAccessGroups(userId),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && Boolean(userId),
  });

export const useCreateAccessGroup = (scope: AccessGroupScope = "tenant") => {
  const queryClient = useQueryClient();

  return useMutation<AccessGroupDetailItem, Error, CreateAccessGroupPayload>({
    mutationFn: (payload) =>
      scope === "admin"
        ? adminAccessGroupsService.createAccessGroup(payload)
        : tenantAdminService.createAccessGroup(payload as CreateAccessGroupDto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessGroupKeys.lists(scope) });
    },
  });
};

export const useUpdateAccessGroup = (scope: AccessGroupScope, id: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    AccessGroupDetail | AccessGroupSummary | AdminAccessGroupSummary,
    Error,
    UpdateAccessGroupDto
  >({
    mutationFn: (payload) =>
      scope === "admin"
        ? adminAccessGroupsService.updateAccessGroup(id, payload)
        : tenantAdminService.updateAccessGroup(id, payload),
    onSuccess: (data) => {
      if (scope === "tenant" && "members" in data) {
        queryClient.setQueryData(accessGroupKeys.detail(scope, id), data);
      }
      queryClient.invalidateQueries({
        queryKey: accessGroupKeys.detail(scope, id),
      });
      queryClient.invalidateQueries({ queryKey: accessGroupKeys.lists(scope) });
    },
  });
};

export const useUpdateAccessGroupStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AccessGroupDetail,
    Error,
    { id: string; isActive: boolean }
  >({
    mutationFn: ({ id, isActive }) =>
      tenantAdminService.updateAccessGroup(id, { isActive }),
    onSuccess: (group) => {
      queryClient.setQueryData(
        accessGroupKeys.detail("tenant", group.id),
        group,
      );
      queryClient.invalidateQueries({
        queryKey: accessGroupKeys.lists("tenant"),
      });
    },
  });
};

export const useReplaceAccessGroupMembers = (
  scope: AccessGroupScope,
  id: string,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    AccessGroupDetail | AdminAccessGroupDetail,
    Error,
    string[]
  >({
    mutationFn: (userIds) =>
      scope === "admin"
        ? adminAccessGroupsService.replaceAccessGroupMembers(id, userIds)
        : tenantAdminService.replaceAccessGroupMembers(id, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accessGroupKeys.detail(scope, id),
      });
    },
  });
};

export const useReplaceAccessGroupTenants = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantIds: string[]) =>
      adminAccessGroupsService.replaceAccessGroupTenants(id, tenantIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accessGroupKeys.detail("admin", id),
      });
    },
  });
};
